import { Temporal } from '@js-temporal/polyfill';

const DAY_NAMES = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'];

const MONTH_NAMES = [
  'Jan',
  'Feb',
  'Mar',
  'Apr',
  'May',
  'Jun',
  'Jul',
  'Aug',
  'Sep',
  'Oct',
  'Nov',
  'Dec',
];

const dayName = (d) => DAY_NAMES[d.dayOfWeek - 1];

const monthName = (d) => MONTH_NAMES[d.month - 1];

const { from: date, compare } = Temporal.PlainDate;

const between = (start, d, end) => compare(start, d) <= 0 && compare(d, end) <= 0;

const dateValues = (obj) => {
  if (typeof obj === 'string') {
    return date(obj);
  } else {
    return Object.fromEntries(Object.entries(obj).map(([k, v]) => [k, dateValues(v)]));
  }
};

class Calendar {
  constructor(data) {
    this.firstDay = date(data.firstDay);
    this.lastDay = date(data.lastDay);
    this.startOfSummer = this.lastDay.add({ days: 1 });
    this.examDays = this.parseExamDays(data.exams); // note, just strings
    this.apExams = dateValues(data.apExams);
    this.holidays = new Set(data.holidays.map((d) => date(d).toString()));
    this.breakNames = data.breakNames;
    Object.assign(this, this.parseYear());
  }

  parseExamDays(exams) {
    const examDays = new Set();
    Object.values(exams).forEach(semester => {
      const { start, end } = semester;
      for (let d = date(start);
           Temporal.PlainDate.compare(d, date(end)) <= 0;
           d = d.add({days: 1}))
      {
        examDays.add(d.toString());
      }
    });
    return examDays;
  }

  /*
   * Parse the year into elements (weeks and vacations which are any chunks of
   * days off longer than three days.) and an array of school days.
   */
  parseYear() {

    const elements = [];
    const daysOfYear = [];

    let monday = this.mondayOfWeekContaining(this.firstDay);
    let w = 1;
    let done = false;

    do {
      const week = this.parseWeek(monday, w++);
      elements.push(week);
      daysOfYear.push(...week.days);

      const breakStart = week.end.add({ days: 1 });
      if (Temporal.PlainDate.compare(breakStart, this.lastDay) < 0) {
        // I.e. week isn't the last week of the school year
        const next = this.nextSchoolDay(week.end);
        const daysOff = next.since(breakStart).total('day');
        if (daysOff > 3) {
          elements.push(new Vacation(this.breakNames[breakStart], daysOff, next));
        }
        // Move to next week.
        monday = this.mondayOfWeekContaining(next);
      } else {
        done = true;
      }
    } while (!done);

    return { elements, daysOfYear };
  }

  mondayOfWeekContaining(d) {
    return d.add({ days: 1 - d.dayOfWeek });
  }

  // Precondition: we're still in the school year
  nextSchoolDay(endOfWeek) {
    if (Temporal.PlainDate.compare(endOfWeek, this.lastDay) >= 0) {
      throw new Error("Can't get nextSchoolDay after end of year.");
    }
    let d = endOfWeek.add({days: 1});
    while (!this.isSchoolDay(d)) {
      d = d.add({days: 1});
    }
    return d;
  }

  /*
   * Fill in a Week object based on the days between a given Monday and the
   * Friday of that week.
   */
  parseWeek(monday, number) {
    const days = [];
    const chunks = [];

    let kind = null;
    let run = 0;

    for (let i = 0; i < 5; i++) {
      const d = monday.add({days: i});
      const newKind = this.kindOfDay(d);
      if (kind === null) kind = newKind;

      if (newKind !== 'off') {
        days.push(d);
      }

      if (newKind != kind && run > 0) {
        chunks.push({[kind]: run});
        kind = newKind;
        run = 0;
      }
      run++;
    }
    if (run > 0) {
      chunks.push({[kind]: run});
    }

    return new Week(days, number, chunks, this.apExams);
  }

  kindOfDay(d) {
    if (this.examDays.has(d.toString())) {
      return 'exams';
    } else if (this.isSchoolDay(d)) {
      return 'regular';
    } else {
      return 'off';
    }
  }

  get schoolWeeks() {
    return this.elements.reduce((acc, w) => acc + (w.isWeek ? 1 : 0), 0);
  }

  get schoolDays() {
    return this.daysOfYear.length;
  }

  isHoliday(d) {
    return this.holidays.has(d.toString());
  }

  isSchoolDay(d) {
    return d.dayOfWeek < 6 && !this.isHoliday(d) && between(this.firstDay, d, this.lastDay);
  }

  nthSchoolDay(n) {
    return this.daysOfYear[n];
  }
}

class Week {
  constructor(days, number, chunks, apExams) {
    this.days = days;
    this.number = number;
    this.chunks = chunks;
    this.start = days[0];
    this.end = days[days.length - 1];
    this.isAP = days.some((d) => between(apExams.start, d, apExams.end));
    this.schoolDays = days.length;
  }

  isWeek = true;

  weekstring() {
    return `${this.daysOfWeek()} (${this.datesOfWeek()})`;
  }

  daysOfWeek() {
    return `${dayName(this.start)}-${dayName(this.end)}`;
  }

  datesOfWeek() {
    return this.start.month === this.end.month
      ? `${monthName(this.start)} ${this.start.day}-${this.end.day}`
      : `${monthName(this.start)} ${this.start.day}-${monthName(this.end)} ${this.end.day}`;
  }

  dump(dumper) {
    dumper.week(this);
  }
}

class Vacation {
  constructor(label, daysOff, dayAfter) {
    this.label = label;
    this.daysOff = daysOff;
    this.dayAfter = dayAfter;
  }

  isWeek = false;

  vacationString() {
    return `${this.vacationLabel()} (${this.daysOff} days)`;
  }

  vacationLabel() {
    return this.label;

    // switch (this.dayAfter.month) {
    //   case 11:
    //     return 'THANKSGIVING BREAK';
    //   case 1:
    //     return 'WINTER BREAK';
    //   case 2:
    //     return 'PRESIDENTS’ DAY WEEKEND';
    //   default:
    //     return 'SPRING BREAK';
    // }
  }

  dump(dumper) {
    dumper.vacation(this);
  }
}

export default Calendar;
